import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { WorkpayApiError } from '@/lib/apiEnvelope';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button, TextInput } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { createDepartment, updateDepartment } from '@/services/departmentService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import type {
  CreateDepartmentPayload,
  DepartmentDto,
} from '@/types/department';

import styles from './DepartmentModals.module.scss';

interface CreateEditDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: DepartmentDto | null;
}

interface DepartmentFormValues {
  name: string;
  code: string;
}

const DEFAULT_VALUES: DepartmentFormValues = { name: '', code: '' };

function CreateEditDepartmentModal({
  isOpen,
  onClose,
  editData,
}: CreateEditDepartmentModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const isEditMode = Boolean(editData);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<DepartmentFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      reset({ name: editData.name, code: editData.code });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, editData, reset]);

  // 409 conflicts can come back as either a name or code duplicate. The BE
  // string-error-code distinguishes them; surface inline next to the offending
  // field. Anything else falls back to a toast.
  const handleMutationError = useCallback(
    (error: unknown) => {
      const e = error as WorkpayApiError;
      // 41043 = WP_DEPARTMENT_CODE_ALREADY_EXISTS, 41042 = WP_DEPARTMENT_NAME_ALREADY_EXISTS.
      if (e.code === 41043) {
        setError('code', { type: 'server', message: t('K_VALIDATION_CODE_DUPLICATE', 'This code is already in use') });
        return;
      }
      if (e.code === 41042) {
        setError('name', { type: 'server', message: t('K_VALIDATION_NAME_DUPLICATE', 'This name is already in use') });
        return;
      }
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
    [addNotification, setError, t],
  );

  const invalidateCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
    void queryClient.invalidateQueries({ queryKey: ['departments-cascade'] });
    // Job title rows render department chips — refresh those too when a
    // department's name/code changes.
    if (isEditMode) {
      void queryClient.invalidateQueries({ queryKey: ['job-titles'] });
    }
  }, [isEditMode, queryClient]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateDepartmentPayload) => createDepartment(payload),
    onSuccess: () => {
      invalidateCaches();
      reset();
      onClose();
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: CreateDepartmentPayload) =>
      updateDepartment(editData!.id, payload),
    onSuccess: () => {
      invalidateCaches();
      reset();
      onClose();
    },
    onError: handleMutationError,
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = useCallback(
    (values: DepartmentFormValues) => {
      const payload: CreateDepartmentPayload = {
        name: values.name.trim(),
        code: values.code.trim(),
      };
      if (isEditMode) {
        updateMutation.mutate(payload);
      } else {
        createMutation.mutate(payload);
      }
    },
    [isEditMode, createMutation, updateMutation],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      reset();
      onClose();
    }
  }, [mutation.isPending, reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditMode
          ? t('K_MODAL_EDIT_DEPARTMENT', 'Edit Department')
          : t('K_MODAL_CREATE_DEPARTMENT', 'Create Department')
      }
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="department-form"
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? t('K_BUTTON_SAVING', 'Saving...')
              : isEditMode
                ? t('K_UPDATE', 'Update')
                : t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      <form id="department-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextInput
          label={t('K_LABEL_NAME', 'Name')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_DEPARTMENT_NAME', 'e.g. Engineering')}
          error={errors.name?.message}
          {...register('name', {
            required: t('K_VALIDATION_NAME_REQUIRED', 'Name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />
        <TextInput
          label={t('K_LABEL_CODE', 'Code')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_DEPARTMENT_CODE', 'e.g. ENG')}
          error={errors.code?.message}
          {...register('code', {
            required: t('K_VALIDATION_CODE_REQUIRED', 'Code is required'),
            maxLength: { value: 100, message: t('K_VALIDATION_MAX_100', 'Max 100 characters') },
          })}
        />
      </form>
    </Modal>
  );
}

export default CreateEditDepartmentModal;
